import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import 'ol/ol.css';
import OlMap from 'ol/Map';
import View from 'ol/View';
import Overlay from 'ol/Overlay';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import OSM from 'ol/source/OSM';
import VectorSource from 'ol/source/Vector';
import GeoJSON from 'ol/format/GeoJSON';
import Feature from 'ol/Feature';
import { Geometry } from 'ol/geom';
import Select from 'ol/interaction/Select';
import { pointerMove } from 'ol/events/condition';
import { fromLonLat } from 'ol/proj';
import { intersects as extentIntersects } from 'ol/extent';

import {
  DATA_PROJECTION,
  RegionCollection,
  RegionFeature,
} from '../regions';
import { KLASSEN, normiereVerteilung, Verteilung } from '../klassen';
import {
  bundeslandStyleFn,
  landkreisStyleFn,
  feldStyleFn,
  hoverStyleFn,
  highlightStyleFn,
  landkreisLinieStyle,
} from '../mapStyles';
import type { Level, Selection } from '../App';

// --- Zoom-Schwellen (§7.2), oben und leicht justierbar ---
export const ZOOM_LANDKREIS = 8; // ab hier Landkreise
const ZOOM_FELD = 11; // ab hier Felder
// Feld-Donuts erst beim starken Heranzoomen zeigen (davor nur die Feldflächen).
const ZOOM_FELD_DONUT = 14;

const START_CENTER = fromLonLat([7.45, 49.95]); // Mitte Rheinland-Pfalz
const START_ZOOM = 7.4;

interface Props {
  bundeslaender: RegionCollection;
  landkreise: RegionCollection;
  loadFelder: (landkreisId: string) => Promise<RegionCollection>;
  selection: Selection;
  onSelect: (selection: Selection) => void;
  onZoomChange?: (zoom: number) => void;
}

const geojsonFormat = new GeoJSON();

/** ha-Wert im deutschen Format (1 Nachkommastelle unter 100, sonst ganzzahlig). */
function formatHa(wert: number): string {
  const dezimal = wert < 100 ? 1 : 0;
  return wert.toLocaleString('de-DE', {
    minimumFractionDigits: dezimal,
    maximumFractionDigits: dezimal,
  });
}

/** Fläche in Hektar aus den Feature-Properties (aus der Datenquelle). */
function flaecheHa(feature: Feature<Geometry>): number | undefined {
  const ha = feature.get('flaecheHa');
  return typeof ha === 'number' ? ha : undefined;
}

/** ha-Kurzform fuer den Tooltip (Bundesland/Landkreis). */
function verteilungHaText(v: Verteilung): string {
  return KLASSEN.map((k) => `${k.label} ${formatHa(v[k.key])} ha`).join(' · ');
}

/** Kurzform der Verteilung für den Tooltip, z. B. „gut 41 % · ok 47 % · schlecht 12 %". */
function verteilungKurz(verteilung: Verteilung | undefined): string {
  const norm = normiereVerteilung(verteilung);
  if (!norm) return '';
  return norm.map((n) => `${n.label} ${Math.round(n.anteil * 100)} %`).join(' · ');
}

/** Feature → Selection-Objekt (mit Fremdschlüsseln für die Sidebar-Kopplung). */
function featureZuSelection(feature: Feature<Geometry>): Selection {
  const level = feature.get('__level') as Level;
  const id = String(feature.get('id'));
  const name = String(feature.get('name') ?? id);
  const verteilung = feature.get('verteilung') as Verteilung | undefined;
  const verteilungHa = feature.get('verteilungHa') as Verteilung | undefined;
  const sizeHa = flaecheHa(feature);
  if (level === 'landkreis') {
    return {
      level,
      id,
      name,
      verteilung,
      verteilungHa,
      sizeHa,
      parentBundeslandId: String(feature.get('bundeslandId') ?? ''),
    };
  }
  if (level === 'feld') {
    return {
      level,
      id,
      name,
      verteilung,
      verteilungHa,
      sizeHa,
      parentLandkreisId: String(feature.get('landkreisId') ?? ''),
    };
  }
  return { level, id, name, verteilung, verteilungHa, sizeHa };
}

const MapView: React.FC<Props> = ({
  bundeslaender,
  landkreise,
  loadFelder,
  selection,
  onSelect,
  onZoomChange,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);

  const mapRef = useRef<OlMap | null>(null);
  const viewRef = useRef<View | null>(null);
  const blSource = useRef(new VectorSource<Geometry>());
  const lkSource = useRef(new VectorSource<Geometry>());
  const feldSource = useRef(new VectorSource<Geometry>());

  const blLayer = useRef<VectorLayer<VectorSource<Geometry>> | null>(null);
  const lkLayer = useRef<VectorLayer<VectorSource<Geometry>> | null>(null);
  const feldLayer = useRef<VectorLayer<VectorSource<Geometry>> | null>(null);

  const featureIndex = useRef(new Map<string, Feature<Geometry>>());
  const geladeneLandkreise = useRef(new Set<string>());
  // Geladene Feld-Features je Landkreis → gezieltes Freigeben beim Schwenken.
  const felderJeLandkreis = useRef(new Map<string, Feature<Geometry>[]>());

  // Aktuelle Auswahl-ID: das gewählte Feature bekommt KEINE Hover-Füllung
  // (nur die Auswahl-Kontur), damit ein Klick wie die Menü-Auswahl aussieht.
  const selectedIdRef = useRef<string | null>(null);
  selectedIdRef.current = selection?.id ?? null;

  // Aktuelle Auswahl in einem Ref (für den Eviction-Handler, ohne Neuaufbau).
  const selectionRef = useRef<Selection>(selection);
  selectionRef.current = selection;

  // Aktuelle Props/Callbacks in Refs (stabile Event-Handler ohne Neuaufbau).
  const propsRef = useRef({ loadFelder, onSelect, onZoomChange });
  propsRef.current = { loadFelder, onSelect, onZoomChange };

  // --- Ebenen-Umschaltung nach Zoom (§7.2) ---
  const aktualisiereEbenen = useRef(() => {
    const view = viewRef.current;
    if (!view || !blLayer.current || !lkLayer.current || !feldLayer.current) return;
    const z = view.getZoom() ?? START_ZOOM;
    blLayer.current.setVisible(z < ZOOM_LANDKREIS);
    // Landkreise ab der LK-Zoomstufe und näher sichtbar: bei [LK, Feld) mit
    // Tönung/Donut, ab Feld-Zoom nur die dünne Grenzlinie (s. lkLayer-Style).
    lkLayer.current.setVisible(z >= ZOOM_LANDKREIS);
    feldLayer.current.setVisible(z >= ZOOM_FELD);
    if (z >= ZOOM_FELD) ladeFelderImViewport.current();
  });

  // --- Felder bedarfsgerecht laden: nur Landkreise im Viewport (§7.2/§5.3) ---
  const ladeFelderImViewport = useRef(() => {
    const map = mapRef.current;
    const view = viewRef.current;
    if (!map || !view) return;
    const viewExtent = view.calculateExtent(map.getSize() ?? [0, 0]);
    lkSource.current.getFeatures().forEach((lk) => {
      const geom = lk.getGeometry();
      if (!geom) return;
      if (!extentIntersects(viewExtent, geom.getExtent())) return;
      const lkId = String(lk.get('id'));
      if (geladeneLandkreise.current.has(lkId)) return;
      geladeneLandkreise.current.add(lkId);
      propsRef.current
        .loadFelder(lkId)
        .then((coll) => {
          const feats = leseFeatures(coll, 'feld');
          feldSource.current.addFeatures(feats);
          feats.forEach((f) => featureIndex.current.set(String(f.get('id')), f));
          felderJeLandkreis.current.set(lkId, feats);
        })
        .catch(() => {
          geladeneLandkreise.current.delete(lkId); // erneuter Versuch möglich
        });
    });
  });

  // --- Nicht mehr sichtbare Felder freigeben (Speicher begrenzen, §7.2) ---
  // Ohne dies wüchse feldSource beim Schwenken unbegrenzt (bis alle ~24.600
  // Felder geladen sind). Behalten werden nur Landkreise im gepufferten Viewport
  // (bei Feld-Zoom) sowie der Landkreis der aktuellen Auswahl; unterhalb des
  // Feld-Zooms werden bis auf die Auswahl alle freigegeben. Der rohe JSON-Cache
  // (regions.ts) bleibt → erneutes Betreten lädt ohne Netzabruf.
  const entferneFelderAusserhalb = useRef(() => {
    const map = mapRef.current;
    const view = viewRef.current;
    if (!map || !view) return;
    const z = view.getZoom() ?? START_ZOOM;
    const sel = selectionRef.current;
    const behalteLk =
      sel?.level === 'feld'
        ? sel.parentLandkreisId
        : sel?.level === 'landkreis'
        ? sel.id
        : undefined;

    // Bei Feld-Zoom: gepufferter Viewport (±1 Bildschirm) als Behalte-Bereich.
    let buffered: [number, number, number, number] | null = null;
    if (z >= ZOOM_FELD) {
      const [minX, minY, maxX, maxY] = view.calculateExtent(map.getSize() ?? [0, 0]);
      const bw = maxX - minX;
      const bh = maxY - minY;
      buffered = [minX - bw, minY - bh, maxX + bw, maxY + bh];
    }

    const zuEntfernen: string[] = [];
    felderJeLandkreis.current.forEach((_feats, lkId) => {
      if (lkId === behalteLk) return;
      if (buffered) {
        const geom = featureIndex.current.get(lkId)?.getGeometry();
        if (geom && extentIntersects(buffered, geom.getExtent())) return;
      }
      zuEntfernen.push(lkId);
    });

    zuEntfernen.forEach((lkId) => {
      felderJeLandkreis.current.get(lkId)?.forEach((f) => {
        feldSource.current.removeFeature(f);
        featureIndex.current.delete(String(f.get('id')));
      });
      felderJeLandkreis.current.delete(lkId);
      geladeneLandkreise.current.delete(lkId);
    });
  });

  // --- Karte + Interaktionen einmalig aufbauen ---
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const view = new View({
      projection: 'EPSG:3857',
      center: START_CENTER,
      zoom: START_ZOOM,
      minZoom: 5,
      maxZoom: 16,
    });
    viewRef.current = view;

    const basemap = new TileLayer({ source: new OSM(), className: 'osm-grayscale' });

    // Auswahl-Hervorhebung wird direkt vom jeweils sichtbaren Basis-Layer
    // gezeichnet (nicht mehr über eine separate Highlight-Ebene): für das
    // gewählte Feature liefert die Style-Fn statt der Klassentönung die
    // Auswahl-Styles (dicke blaue Kontur + Donut, `highlightStyleFn`). Da die
    // Basis-Layer im Klick- wie im Menü-Pfad zuverlässig rendern, erscheint die
    // Kontur in beiden Fällen. Die Zoom-Sichtbarkeit der LK-Kontur ergibt sich
    // von selbst: lkLayer ist nur bei z ∈ [ZOOM_LANDKREIS, ZOOM_FELD) sichtbar,
    // also blendet die dicke LK-Linie oberhalb ZOOM_FELD automatisch aus.
    const istAuswahl = (feature: Feature<Geometry>) =>
      String(feature.get('id')) === selectedIdRef.current;

    // Auswahl-Styles je Ebene/Zoom — EINE Quelle für Basis-Layer UND Hover.
    // Wichtig: Der Hover (ol/interaction/Select) setzt einen Feature-Style
    // (feature.setStyle), der den Layer-Style ÜBERSCHREIBT. Würde der Hover für
    // das gewählte Feature [] liefern, verschwänden Kontur/Donut beim Überfahren.
    // Deshalb liefert der Hover fürs gewählte Feature exakt dieselben Styles wie
    // der Basis-Layer → beim Überfahren ändert sich optisch nichts (dicke Kontur
    // + Donut bleiben), nur das Tooltip erscheint zusätzlich.
    const auswahlStyles = (feature: Feature<Geometry>, z: number) => {
      const level = (feature.get('__level') as Level) || 'landkreis';
      if (level === 'bundesland') return highlightStyleFn(feature, z < ZOOM_LANDKREIS, true);
      if (level === 'feld') {
        const marker = z >= ZOOM_FELD_DONUT ? 'donut' : 'kreis';
        // Feld-Auswahl behält Klassenfarbe (Füllung + Donut/Kreis) + dicke Kontur.
        return [...feldStyleFn(feature, marker), ...highlightStyleFn(feature, false, true)];
      }
      // Landkreis: ab Feld-Zoom nur die dünne Grenzlinie (dicke Kontur blendet aus).
      if (z >= ZOOM_FELD) return [landkreisLinieStyle];
      return highlightStyleFn(feature, z >= ZOOM_LANDKREIS && z < ZOOM_FELD, z <= ZOOM_FELD);
    };

    blLayer.current = new VectorLayer({
      source: blSource.current,
      style: ((feature: Feature<Geometry>) => {
        const z = viewRef.current?.getZoom() ?? START_ZOOM;
        if (istAuswahl(feature)) return auswahlStyles(feature, z);
        return bundeslandStyleFn(feature);
      }) as any,
    });
    lkLayer.current = new VectorLayer({
      source: lkSource.current,
      // declutter blendet nur kollidierende Icon-/Text-Stile aus (Donuts), nicht
      // die Polygon-Tönung. So verschwinden bei eng benachbarten Landkreisen
      // (v. a. kreisfreie Städte) überlappende Donuts; die Klassenfarbe bleibt.
      declutter: true,
      style: ((feature: Feature<Geometry>) => {
        const z = viewRef.current?.getZoom() ?? START_ZOOM;
        if (istAuswahl(feature)) return auswahlStyles(feature, z);
        // Ab Feld-Zoom nur die dünne LK-Grenzlinie (keine Tönung/Donut).
        if (z >= ZOOM_FELD) return [landkreisLinieStyle];
        return landkreisStyleFn(feature);
      }) as any,
    });
    feldLayer.current = new VectorLayer({
      source: feldSource.current,
      // Donut erst ab ZOOM_FELD_DONUT (starkes Heranzoomen); im Zwischenzoom
      // (Feld sichtbar, aber noch kein Donut) ein farbiger Kreis je Feld.
      style: ((feature: Feature<Geometry>) => {
        const z = viewRef.current?.getZoom() ?? START_ZOOM;
        if (istAuswahl(feature)) return auswahlStyles(feature, z);
        return feldStyleFn(feature, z >= ZOOM_FELD_DONUT ? 'donut' : 'kreis');
      }) as any,
      declutter: true,
    });

    const tooltipOverlay = new Overlay({
      element: tooltipRef.current!,
      offset: [12, 0],
      positioning: 'center-left',
      stopEvent: false,
    });

    const map = new OlMap({
      target: containerRef.current,
      layers: [basemap, blLayer.current, lkLayer.current, feldLayer.current],
      view,
      overlays: [tooltipOverlay],
    });
    mapRef.current = map;

    // Aktuell überfahrenes Feature (fürs Tooltip-Nachführen ohne zweiten
    // Hit-Test) + letzte Cursor-Koordinate (Tooltip sofort korrekt platzieren).
    let hovertreffer: Feature<Geometry> | null = null;
    let letzteKoordinate: number[] | undefined;

    // Hover: Select mit pointerMove → Hover-Stil (Markenblau). Das aktuell
    // gewählte Feature wird NICHT gefüllt (nur die Auswahl-Kontur bleibt).
    const hover = new Select({
      condition: pointerMove,
      // Bei Feld-Zoom ist die LK-Ebene nur eine Grenzlinie (kein Klick-/Hover-
      // Ziel): sonst würde das Überfahren einer Lücke zwischen Feldern den ganzen
      // Landkreis blau füllen. Felder/BL bleiben normal überfahrbar.
      filter: ((feature: Feature<Geometry>) => {
        const z = viewRef.current?.getZoom() ?? START_ZOOM;
        return !(z >= ZOOM_FELD && (feature.get('__level') as Level) === 'landkreis');
      }) as any,
      style: ((feature: Feature<Geometry>) => {
        // Gewähltes Feature: KEINE Hover-Füllung, sondern exakt die Auswahl-Styles
        // (Select überschreibt via feature.setStyle den Layer-Style — [] würde die
        // dicke Kontur/Donut löschen). So bleibt die Auswahl beim Überfahren stabil.
        if (istAuswahl(feature)) {
          const z = viewRef.current?.getZoom() ?? START_ZOOM;
          return auswahlStyles(feature, z);
        }
        return hoverStyleFn(feature);
      }) as any,
      layers: [blLayer.current, lkLayer.current, feldLayer.current],
    });
    map.addInteraction(hover);

    // Verlässt der Cursor die Karte (z. B. in die Sidebar), Hover-Visuals lösen:
    // Select-Hervorhebung leeren, Tooltip ausblenden, Cursor zurücksetzen.
    const viewport = map.getViewport();
    const onPointerLeave = () => {
      hover.getFeatures().clear();
      hovertreffer = null;
      const el = tooltipRef.current;
      if (el) el.style.display = 'none';
      map.getTargetElement().style.cursor = '';
    };
    viewport.addEventListener('mouseleave', onPointerLeave);

    // Tooltip-Inhalt aufbauen: Feld -> Name + Fläche(ha) + Verteilung(%),
    // Bundesland/Landkreis -> Name + Verteilung in ha.
    const renderTooltip = (feature: Feature<Geometry>) => {
      const el = tooltipRef.current;
      if (!el) return;
      const level = feature.get('__level') as Level;
      const id = String(feature.get('id') ?? '');
      const name = String(feature.get('name') ?? id);
      let kopf = name;
      let detail = '';
      if (level === 'feld') {
        const ha = flaecheHa(feature);
        if (ha != null) kopf = `${name} · ${formatHa(ha)} ha`;
        detail = verteilungKurz(feature.get('verteilung') as Verteilung | undefined);
      } else {
        const ha = feature.get('verteilungHa') as Verteilung | undefined;
        detail = ha
          ? verteilungHaText(ha)
          : verteilungKurz(feature.get('verteilung') as Verteilung | undefined);
      }
      // Kein innerHTML: Werte kommen aus Daten → als Textknoten setzen
      // (robust gegen etwaige HTML-Sonderzeichen in Namen).
      el.textContent = '';
      const stark = document.createElement('strong');
      stark.textContent = kopf;
      el.appendChild(stark);
      if (detail) {
        el.appendChild(document.createElement('br'));
        el.appendChild(document.createTextNode(detail));
      }
      el.style.display = 'block';
    };

    // Tooltip-Inhalt + Cursor folgen dem Select-Hover: EIN Hit-Test statt zwei.
    // 'select' feuert nur, wenn sich das überfahrene Feature ändert.
    hover.on('select', () => {
      const feats = hover.getFeatures().getArray();
      hovertreffer = feats.length ? (feats[feats.length - 1] as Feature<Geometry>) : null;
      const el = tooltipRef.current;
      if (hovertreffer) {
        renderTooltip(hovertreffer);
        if (letzteKoordinate) tooltipOverlay.setPosition(letzteKoordinate);
        map.getTargetElement().style.cursor = 'pointer';
      } else {
        if (el) el.style.display = 'none';
        map.getTargetElement().style.cursor = '';
      }
    });

    // Tooltip-Position dem Cursor nachführen — ohne erneuten Hit-Test.
    map.on('pointermove', (evt) => {
      if (evt.dragging) return;
      letzteKoordinate = evt.coordinate;
      if (hovertreffer) tooltipOverlay.setPosition(evt.coordinate);
    });

    // Klick: getroffenes Feature → Auswahl melden (Drill-down via view.fit, §8).
    map.on('singleclick', (evt) => {
      let treffer: Feature<Geometry> | undefined;
      map.forEachFeatureAtPixel(
        evt.pixel,
        (f) => {
          treffer = f as Feature<Geometry>;
          return true;
        },
        {
          // Bei Feld-Zoom die LK-Grenzlinie nicht anklickbar machen: ein Klick in
          // eine Lücke zwischen Feldern soll nicht den Landkreis wählen (und
          // damit wieder herauszoomen).
          layerFilter: (l) => {
            const z = viewRef.current?.getZoom() ?? START_ZOOM;
            return !(z >= ZOOM_FELD && l === lkLayer.current);
          },
        }
      );
      if (treffer) propsRef.current.onSelect(featureZuSelection(treffer));
    });

    // Ebenen-Umschaltung + Zoom-Meldung bei jeder Bewegung.
    const meldeZoom = () => {
      const z = view.getZoom();
      if (z != null) propsRef.current.onZoomChange?.(z);
    };
    map.on('moveend', () => {
      aktualisiereEbenen.current();
      entferneFelderAusserhalb.current();
      meldeZoom();
    });
    aktualisiereEbenen.current();
    meldeZoom();

    return () => {
      viewport.removeEventListener('mouseleave', onPointerLeave);
      map.setTarget(undefined);
      mapRef.current = null;
    };
  }, []);

  // --- Bundesländer laden ---
  useEffect(() => {
    const feats = leseFeatures(bundeslaender, 'bundesland');
    blSource.current.clear();
    blSource.current.addFeatures(feats);
    feats.forEach((f) => featureIndex.current.set(String(f.get('id')), f));
  }, [bundeslaender]);

  // --- Landkreise laden ---
  useEffect(() => {
    const feats = leseFeatures(landkreise, 'landkreis');
    lkSource.current.clear();
    lkSource.current.addFeatures(feats);
    feats.forEach((f) => featureIndex.current.set(String(f.get('id')), f));
  }, [landkreise]);

  // --- Auswahl → Kontur (im Basis-Layer) + nahtloses Zoomen (§8) ---
  useEffect(() => {
    const view = viewRef.current;
    // Basis-Layer neu zeichnen, damit die (De-)Auswahl ihre Kontur/Tönung
    // aktualisiert (die Layer-Style-Fn liest selectedIdRef und zeichnet für das
    // gewählte Feature die Auswahl-Styles statt der Klassentönung).
    blSource.current.changed();
    lkSource.current.changed();
    feldSource.current.changed();
    if (!selection || !view) return;

    // Nahtlos auf ein Feature zoomen (§8). Bei Bundesland-Auswahl den Zoom unter
    // die LK-Schwelle begrenzen, damit die Karte in der BL-Ebene bleibt.
    const zoomeAuf = (feature: Feature<Geometry>) => {
      const geom = feature.getGeometry();
      if (!geom) return;
      // BL bleibt in der BL-Ebene, LK bleibt in der LK-Ebene (knapp unter der
      // Feld-Schwelle) → der Auto-Zoom auf einen LK zeigt IMMER die dicke Linie.
      const maxZoom = selection.level === 'bundesland' ? ZOOM_LANDKREIS - 0.5 : ZOOM_FELD - 0.5;
      view.fit(geom.getExtent(), { duration: 600, padding: [48, 48, 48, 48], maxZoom });
    };

    // Feld-Auswahl: Zoom bleibt unverändert. Ist das Feld noch nicht geladen,
    // den Landkreis nachladen und danach neu einfärben, damit die Kontur greift.
    if (selection.level === 'feld') {
      if (featureIndex.current.get(selection.id)) return;
      if (selection.parentLandkreisId) {
        propsRef.current.loadFelder(selection.parentLandkreisId).then((coll) => {
          const feats = leseFeatures(coll, 'feld');
          if (!geladeneLandkreise.current.has(selection.parentLandkreisId!)) {
            feldSource.current.addFeatures(feats);
            felderJeLandkreis.current.set(selection.parentLandkreisId!, feats);
            geladeneLandkreise.current.add(selection.parentLandkreisId!);
          }
          feats.forEach((f) => featureIndex.current.set(String(f.get('id')), f));
          feldSource.current.changed();
        });
      }
      return;
    }

    // Bundesland/Landkreis: nahtlos heranzoomen (Kontur zeichnet der Basis-Layer).
    const vorhanden = featureIndex.current.get(selection.id);
    if (vorhanden) zoomeAuf(vorhanden);
  }, [selection]);

  return (
    <Box className="map-pane">
      <div ref={containerRef} className="map-canvas" />
      <div ref={tooltipRef} className="map-tooltip" style={{ display: 'none' }} />
    </Box>
  );
};

/** Liest eine GeoJSON-Collection (s. DATA_PROJECTION) in EPSG:3857 und markiert die Ebene. */
function leseFeatures(coll: RegionCollection, level: Level): Feature<Geometry>[] {
  if (!coll || !coll.features || coll.features.length === 0) return [];
  const feats = geojsonFormat.readFeatures(coll as unknown as object, {
    // Daten liegen bereits in EPSG:3857 vor (s. DATA_PROJECTION) → keine
    // Reprojektion beim Einlesen. Ein 4326-Backend würde DATA_PROJECTION setzen.
    dataProjection: DATA_PROJECTION,
    featureProjection: 'EPSG:3857',
  }) as Feature<Geometry>[];
  feats.forEach((f, i) => {
    f.set('__level', level, true);
    // GeoJSON-"id" liegt in properties; sicherstellen, dass sie als Attribut da ist.
    const props = coll.features[i]?.properties as RegionFeature['properties'] | undefined;
    if (props && f.get('id') == null) f.set('id', props.id, true);
  });
  return feats;
}

export default MapView;
